import { Controller } from "@hotwired/stimulus"

// Gathers the side cards into a straight stack behind the front card, then
// fans them out, tilting as they go, once the stack scrolls into view. The
// fanned layout is the markup's resting state, so the cards stay correct if
// this controller never runs.
export default class extends Controller {
  static targets = ["card"]

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    if (this.prefersReducedMotion || !this.element.animate || typeof IntersectionObserver === "undefined") return

    this.fanned = this.cardTargets.map((card) => card.style.transform)
    this.cardTargets.forEach((card, index) => {
      card.style.transform = this.stackedTransform(index)
    })

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.fanOut()
    }, { threshold: 0.6 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.restore()
  }

  fanOut() {
    this.observer.disconnect()

    this.animations = this.cardTargets.map((card, index) => card.animate(
      [{ transform: this.stackedTransform(index) }, { transform: this.fanned[index] }],
      { duration: 700, delay: 450, easing: "cubic-bezier(0.34, 1.32, 0.64, 1)", fill: "backwards" }
    ))
    this.restore()
  }

  restore() {
    if (!this.fanned) return

    this.cardTargets.forEach((card, index) => {
      card.style.transform = this.fanned[index]
    })
  }

  // A straight stack: the fanned transform with every offset, tilt, and the
  // scale zeroed out. Keeping the same function list makes the browser
  // interpolate each one on its own, so the tilt animates instead of snapping.
  stackedTransform(index) {
    const fanned = this.fanned[index]
    if (!fanned || fanned === "none") return "none"

    return fanned
      .replace(/translate([XY])\([^)]*\)/g, "translate$1(0px)")
      .replace(/rotate\([^)]*\)/, "rotate(0deg)")
      .replace(/scale\([^)]*\)/, "scale(0.94)")
  }

  get prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  }
}
